import React from 'react';
import { Progress, Tag } from 'antd';
import {
  CheckCircleFilled,
  PlayCircleOutlined,
  FileTextOutlined,
  FormOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { toast } from '../../../utils/toast.js';
import { ModuleGateTag } from './ModuleGateTag.jsx';
import { isGateBlocked, gateMessage, PASS_RULE_TEXT } from '../utils/moduleGate.js';

export function LessonSidebar({
  course,
  learningPath,
  syllabus = [],
  currentLessonId,
  completedLessonIds = [],
  progress,
  quiz,
}) {
  const navigate = useNavigate();
  const { isAdmin, isInstructor } = useAuth();

  const getLessonIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'VIDEO':
        return <PlayCircleOutlined style={{ color: '#0284c7' }} />;
      case 'QUIZ':
        return <FormOutlined style={{ color: '#f59e0b' }} />;
      default:
        return <FileTextOutlined style={{ color: '#10b981' }} />;
    }
  };

  const handleSelectLesson = (lesson, targetCourseId) => {
    if (isGateBlocked(lesson.gate) && !isAdmin && !isInstructor) {
      toast.info(gateMessage(lesson.gate));
      return;
    }
    if (learningPath) {
      navigate(`/learn/paths/${learningPath.id}/lessons/${lesson.id}`);
    } else {
      navigate(`/learn/${targetCourseId || course?.id}/lessons/${lesson.id}`);
    }
  };

  // Nếu đang ở chế độ Lộ Trình Học (Learning Path Mode)
  if (learningPath) {
    const items = learningPath.items || [];
    const completedCount = items.filter((item) => completedLessonIds.includes(item.lesson?.id)).length;
    const totalCount = items.length;
    const pathCompletionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return (
      <aside
        style={{
          width: '100%',
          background: '#ffffff',
          borderLeft: '1px solid #e2e8f0',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Learning Path Title & Progress Header */}
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Tag color="purple" style={{ fontWeight: 700, margin: 0 }}>
              {learningPath.code || 'LỘ TRÌNH'}
            </Tag>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Toàn Bộ Lộ Trình Đóng Gói
            </span>
          </div>

          <h3
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: '#0f172a',
              marginBottom: 8,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {learningPath.title}
          </h3>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: '#64748b' }}>Tiến độ: {completedCount}/{totalCount} module</span>
            <strong style={{ color: '#7c3aed' }}>{pathCompletionRate}%</strong>
          </div>
          <Progress percent={pathCompletionRate} showInfo={false} strokeColor="#7c3aed" size="small" />

          {pathCompletionRate >= 100 && (
            <Tag color="green" icon={<TrophyOutlined />} style={{ marginTop: 8, width: '100%', textAlign: 'center' }}>
              Chúc mừng bạn đã hoàn thành lộ trình!
            </Tag>
          )}
        </div>

        {/* Full Path Modules List (12 or 15 modules in sequence) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', padding: '4px 8px 8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Thứ tự {totalCount} Module trong Lộ Trình:
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((item, idx) => {
              const lesson = item.lesson;
              if (!lesson) return null;

              const isCurrent = lesson.id === currentLessonId;
              const isCompleted = completedLessonIds.includes(lesson.id);

              return (
                <div
                  key={item.id || lesson.id}
                  onClick={() => handleSelectLesson(lesson, lesson.courseId)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: isCurrent ? '#f5f3ff' : '#ffffff',
                    border: '1px solid',
                    borderColor: isCurrent ? '#c4b5fd' : isCompleted ? '#bbf7d0' : '#e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = '#faf5ff';
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <div style={{ marginTop: 2 }}>
                    {isCompleted ? (
                      <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} />
                    ) : (
                      getLessonIcon(lesson.lessonType)
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <Tag
                        color={isCurrent ? 'purple' : 'blue'}
                        style={{ fontSize: 11, fontWeight: 700, padding: '0 4px', margin: 0, lineHeight: '18px' }}
                      >
                        {idx + 1}. {lesson.code || `M${idx + 1}`}
                      </Tag>
                      <ModuleGateTag gate={lesson.gate} style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }} />
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent ? '#6d28d9' : '#1e293b',
                        lineHeight: 1.35,
                      }}
                    >
                      {lesson.title.replace(/^[A-Z0-9]+\s*—\s*/, '')}
                    </div>

                    {lesson.course?.title && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {lesson.course.title}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </aside>
    );
  }

  // Chế độ Khóa Học Chuyên Đề (Single Course Mode)
  const completionRate = progress?.completionPercentage || 0;

  return (
    <aside
      style={{
        width: '100%',
        background: '#ffffff',
        borderLeft: '1px solid #e2e8f0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Course Title & Progress Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <h3
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: '#0f172a',
            marginBottom: 8,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {course?.title}
        </h3>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: '#64748b' }}>Tiến độ hoàn thành:</span>
          <strong style={{ color: '#0284c7' }}>{completionRate}%</strong>
        </div>
        <Progress percent={completionRate} showInfo={false} strokeColor="#0284c7" size="small" />

        {progress?.isCompleted && (
          <Tag color="green" icon={<TrophyOutlined />} style={{ marginTop: 8, width: '100%', textAlign: 'center' }}>
            Đã hoàn thành khóa học!
          </Tag>
        )}
      </div>

      {/* Syllabus List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {syllabus.map((module, mIdx) => (
          <div key={module.id || mIdx} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#475569',
                padding: '4px 8px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
              }}
            >
              {module.title}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {module.lessons?.map((lesson) => {
                const isCurrent = lesson.id === currentLessonId;
                const isCompleted = completedLessonIds.includes(lesson.id);

                return (
                  <div
                    key={lesson.id}
                    onClick={() => handleSelectLesson(lesson, course?.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 6,
                      background: isCurrent ? '#f0fdf4' : 'transparent',
                      border: isCurrent ? '1px solid #bbf7d0' : '1px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent) e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} />
                    ) : (
                      getLessonIcon(lesson.lessonType)
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: isCurrent ? 700 : 500,
                          color: isCurrent ? '#005537' : '#334155',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {lesson.code && !lesson.title.startsWith(lesson.code) ? `[${lesson.code}] ` : ''}
                        {lesson.title}
                      </div>
                      {lesson.gate && lesson.gate.status !== 'OPEN' && (
                        <div style={{ marginTop: 3 }}>
                          <ModuleGateTag gate={lesson.gate} style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Quiz Button at bottom of syllabus if exists */}
        {quiz && (
          <div style={{ marginTop: 16, padding: '0 4px' }}>
            <div
              onClick={() => navigate(`/quiz/${quiz.id}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 8,
                background: '#fef3c7',
                border: '1px solid #fde68a',
                cursor: 'pointer',
              }}
            >
              <FormOutlined style={{ color: '#d97706', fontSize: 18 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                  {quiz.title}
                </div>
                <div style={{ fontSize: 11, color: '#b45309' }}>
                  Bài kiểm tra đánh giá ({PASS_RULE_TEXT})
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
